// Honest capability-state view of an external resource. It never FAKES an
// embedded experience: the real domain stays visible, and a site is framed only
// when its provider publishes an embeddable form (see lib/providerEmbeds.ts).
// Everything else keeps the external card, which is a real state rather than a
// failure — a frame that silently refuses to load would be strictly worse.
import React from 'react';
import { ExternalLink, ShieldAlert, Ban, Globe, Lock } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { openExternal, faviconFor, CAPABILITY_IS_OPENABLE } from '../lib/urlPolicy';
import { providerGuidanceFor } from '../lib/providerEmbeds';
import { WebActionBar } from './WebActionBar';
import type { UrlClassification, WorkContext } from '../lib/types';

interface Props {
  resource: UrlClassification;
  title: string;
  workContext?: WorkContext | null;
  onSendToConversation: () => void;
}

export const WebResourceView: React.FC<Props> = ({ resource, title, workContext, onSendToConversation }) => {
  const { capability, domain, reason, url } = resource;
  const favicon = faviconFor(domain);

  const state = (() => {
    switch (capability) {
      case 'UNSAFE_URL':
        return {
          icon: <ShieldAlert className="h-7 w-7 text-danger" />,
          heading: 'This link can’t be opened',
          body: reason ?? 'The address looks unsafe.',
          tone: 'danger' as const,
        };
      case 'BLOCKED_BY_POLICY':
        return {
          icon: <Ban className="h-7 w-7 text-warning" />,
          heading: 'Blocked by your organization',
          body: reason ?? 'Your organization’s policy prevents opening this site.',
          tone: 'warning' as const,
        };
      case 'AUTH_REQUIRED':
        return {
          icon: <Lock className="h-7 w-7 text-muted-foreground" />,
          heading: 'Sign-in required',
          body: 'This site needs you to sign in. Open it securely in your browser.',
          tone: 'neutral' as const,
        };
      case 'EXTERNAL_ONLY':
      default: {
        // ⚠️ Be specific when we CAN be. Telling someone who pasted
        // youtube.com that "this site doesn't support embedded viewing" is
        // false and leaves them nothing to try — YouTube embeds videos, just
        // not its feed. Name the provider and what to paste instead.
        const guidance = providerGuidanceFor(url);
        return {
          icon: <Globe className="h-7 w-7 text-primary-600" />,
          heading: guidance ? `This ${guidance.provider} page can’t be embedded` : 'Opens in your browser',
          body: guidance
            ? `${guidance.hint} In the meantime you can open it securely — your work context stays here.`
            : 'This site doesn’t support embedded viewing inside KaziPeople. Open it securely — your work context stays here.',
          tone: 'neutral' as const,
        };
      }
    }
  })();

  const embed = capability === 'EMBED_SUPPORTED' ? resource.embed : undefined;
  // ⚠️ MUST agree with WebActionBar's CAPABILITY_IS_OPENABLE, plus the
  // sign-in case which only this card offers. The reason this is not just
  // EXTERNAL_ONLY: an EMBED_SUPPORTED classification can arrive from the
  // SERVER with no `embed` payload, and it then falls through to the card
  // below. Without EMBED_SUPPORTED here that card reads "Opens in your
  // browser" with no button to open it — a dead end that looks like a
  // decision.
  const openable = CAPABILITY_IS_OPENABLE(capability) || capability === 'AUTH_REQUIRED';

  return (
    <div className="space-y-4">
      {/* Domain identity bar — real domain always visible (anti-phishing). */}
      <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3">
        {favicon ? (
          <img src={favicon} alt="" className="h-6 w-6 rounded" onError={(e) => ((e.currentTarget.style.display = 'none'))} />
        ) : (
          <Globe className="h-6 w-6 text-muted-foreground" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
            <Lock className="h-3.5 w-3.5 text-success" />
            <span className="truncate">{domain ?? url}</span>
          </div>
          <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">External site</div>
        </div>
      </div>

      {/* Embedded provider content. Replaces the state card — a frame AND a
          "opens in your browser" card on the same screen would contradict each
          other. */}
      {embed ? (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <div className="flex items-center gap-2 border-b border-border px-3 py-2 text-[11px] text-muted-foreground">
            <Globe className="h-3.5 w-3.5" />
            {/* Name the publisher: an embed must never be anonymous, or the
                frame becomes an unattributed surface inside KaziPeople. */}
            <span>Embedded from <span className="font-medium text-foreground">{embed.provider}</span></span>
            <button type="button" onClick={() => openExternal(url)}
              className="ml-auto inline-flex items-center gap-1 underline hover:text-foreground">
              <ExternalLink className="h-3 w-3" /> Open externally
            </button>
          </div>
          <iframe
            src={embed.embedUrl}
            title={title || embed.provider}
            className={embed.aspect === 'video' ? 'aspect-video w-full' : 'h-[70vh] w-full'}
            /* ⚠️ SANDBOXED, and the omissions are the point: no
               allow-top-navigation (a frame must never move the KaziPeople tab
               it sits in), no allow-forms and no allow-modals (nothing framed
               should be collecting input or interrupting). allow-same-origin is
               safe here ONLY because every provider is cross-origin to this
               app — it would defeat the sandbox on a same-origin frame. */
            sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            referrerPolicy="strict-origin-when-cross-origin"
            loading="lazy"
          />
          {/* ⚠️ A FRAME CANNOT TELL YOU IT IS EMPTY. Verified on dev: a Vimeo
              video whose owner restricted embedding frames "successfully" and
              renders a blank white box — no error, no event, nothing the page
              can detect. Same for a Google Doc the viewer cannot open. We
              cannot know it happened, so we must not let a blank box read as
              "there is nothing here". This line is the only honest move left:
              name the likely cause and keep the way out visible. */}
          <p className="border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
            Nothing showing? {embed.provider} may not allow this
            {embed.aspect === 'video' ? ' video' : ' document'} to be embedded, or it may need
            permission.{' '}
            <button type="button" onClick={() => openExternal(url)} className="underline hover:text-foreground">
              Open it externally
            </button>{' '}
            to check.
          </p>
        </div>
      ) : (
      <div className="rounded-xl border border-border bg-card p-6 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-muted">{state.icon}</div>
        <h2 className="mt-3 text-lg font-bold text-foreground">{state.heading}</h2>
        <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{state.body}</p>
        {openable && (
          <Button className="mt-4" onClick={() => openExternal(url)}>
            <ExternalLink className="mr-1.5 h-4 w-4" /> Open externally
          </Button>
        )}
      </div>
      )}

      {/* Actions */}
      <WebActionBar resource={resource} title={title} workContext={workContext} onSendToConversation={onSendToConversation} />
    </div>
  );
};
